import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Upload, Download, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react'

const API = ''

interface BulkResult {
  processed: number
  created: number
  updated: number
  skipped: number
  errorCount: number
  errors: { row: number; sku: string; message: string }[]
}

export function LlantasBulkUpload() {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<BulkResult | null>(null)
  const [error, setError] = useState('')

  async function upload() {
    if (!file) return
    setUploading(true)
    setError('')
    setResult(null)
    const fd = new FormData()
    fd.append('file', file)
    try {
      const res = await fetch(`${API}/api/admin/llantas/bulk`, {
        method: 'POST',
        body: fd,
      })
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || `HTTP ${res.status}`)
        return
      }
      setResult(data)
    } catch (e: any) {
      setError(e.message || 'Error de red')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex items-center gap-3">
        <Link
          to="/llantas"
          className="rounded-lg border border-ink-200 bg-white p-2 hover:bg-ink-50"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-extrabold text-ink-900">
            <Upload className="size-6 text-brand-600" />
            Carga masiva de llantas
          </h1>
          <p className="text-sm text-ink-500">
            Sube un CSV con las columnas estándar. Crea o actualiza por SKU.
          </p>
        </div>
      </header>

      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="mt-0.5 size-5 text-brand-600" />
          <div className="flex-1">
            <h2 className="font-semibold text-ink-900">1. Descarga la plantilla</h2>
            <p className="text-sm text-ink-500">
              La plantilla CSV incluye todas las columnas que el sistema espera, con 2 ejemplos
              ya capturados para que sólo modifiques los datos.
            </p>
            <a
              href={`${API}/api/admin/llantas/template`}
              className="mt-2 inline-flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-semibold text-brand-700 hover:border-brand-300"
            >
              <Download className="size-4" /> plantilla-llantas.csv
            </a>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-ink-200 bg-white p-5">
        <h2 className="mb-3 font-semibold text-ink-900">2. Sube tu CSV</h2>
        <label
          htmlFor="csv-file"
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-ink-200 bg-ink-50 px-6 py-12 hover:border-brand-400 hover:bg-brand-50/50"
        >
          <Upload className="size-10 text-ink-400" />
          <div className="text-center">
            <p className="font-semibold text-ink-700">
              {file ? file.name : 'Arrastra el CSV aquí o haz clic para seleccionar'}
            </p>
            <p className="mt-1 text-xs text-ink-500">
              CSV separado por coma, primera fila = encabezados
            </p>
          </div>
          <input
            id="csv-file"
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>

        {file && (
          <div className="mt-4 flex items-center justify-between rounded-lg border border-ink-200 bg-ink-50 p-3 text-sm">
            <div>
              <div className="font-medium text-ink-900">{file.name}</div>
              <div className="text-xs text-ink-500">{(file.size / 1024).toFixed(1)} KB</div>
            </div>
            <button
              type="button"
              onClick={upload}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              <Upload className="size-4" />
              {uploading ? 'Procesando…' : 'Subir y procesar'}
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 size-4" />
            {error}
          </div>
        )}
      </section>

      {result && (
        <section className="rounded-2xl border border-ink-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2 border-b border-ink-100 pb-3">
            <CheckCircle2 className="size-5 text-emerald-600" />
            <h2 className="font-semibold text-ink-900">Resultado del proceso</h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat label="Procesadas" value={result.processed} />
            <Stat label="Creadas" value={result.created} color="emerald" />
            <Stat label="Actualizadas" value={result.updated} color="brand" />
            <Stat label="Omitidas" value={result.skipped} color="ink" />
            <Stat label="Errores" value={result.errorCount} color={result.errorCount ? 'red' : 'ink'} />
          </div>

          {result.errors.length > 0 && (
            <div className="mt-4">
              <h3 className="mb-2 text-sm font-semibold text-red-700">Errores detectados:</h3>
              <ul className="space-y-1 text-xs">
                {result.errors.map((e, i) => (
                  <li key={i} className="rounded border border-red-100 bg-red-50 p-2">
                    <span className="font-mono">Fila {e.row}</span> ·{' '}
                    <span className="font-mono">{e.sku || '(sin sku)'}</span>:{' '}
                    <span className="text-red-700">{e.message.split('\n')[0]}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4">
            <Link
              to="/llantas"
              className="inline-flex items-center gap-2 rounded-lg bg-ink-900 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
            >
              Ver llantas cargadas
            </Link>
          </div>
        </section>
      )}
    </div>
  )
}

function Stat({ label, value, color = 'ink' }: { label: string; value: number; color?: string }) {
  const colors: Record<string, string> = {
    ink: 'text-ink-900 bg-ink-50',
    emerald: 'text-emerald-700 bg-emerald-50',
    brand: 'text-brand-700 bg-brand-50',
    red: 'text-red-700 bg-red-50',
  }
  return (
    <div className={`rounded-xl p-3 text-center ${colors[color]}`}>
      <div className="text-2xl font-extrabold">{value.toLocaleString('es-MX')}</div>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
    </div>
  )
}
